import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { educationApi, blogApi, settingsApi, initApi } from '@/services/api';

const AdminContext = createContext(null);

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};

// Admin password from environment variable (fallback for development only)
const DEFAULT_ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || 'admin';

// Default display settings
const DEFAULT_SETTINGS = {
  showReasoningInResults: true,
  showReasoningInDetails: true,
  showReasoningInComparison: true,
  showConfidenceIndicators: true,
};

export const AdminProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [displaySettings, setDisplaySettings] = useState(DEFAULT_SETTINGS);
  const [educationSections, setEducationSections] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [apiError, setApiError] = useState(null);

  // Load data from backend API on mount
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      setApiError(null);
      
      try {
        // Try to seed initial data first
        await initApi.seed().catch(() => {});
        
        // Load education sections
        const sections = await educationApi.getAll();
        setEducationSections(sections.map(s => ({
          ...s,
          published: s.published ?? true,
        })));
        
        // Load blog posts from admin endpoint (include content for Education page)
        const posts = await blogApi.getAll(true);
        setBlogPosts((posts || []).map(p => ({
          ...p,
          publishedAt: p.published_at || p.publishedAt,
          published: p.is_published ?? p.published ?? false,
        })));
        
        // Load display settings
        const settings = await settingsApi.getDisplay();
        setDisplaySettings({
          showReasoningInResults: settings.show_reasoning_in_results ?? true,
          showReasoningInDetails: settings.show_reasoning_in_details ?? true,
          showReasoningInComparison: settings.show_reasoning_in_comparison ?? true,
          showConfidenceIndicators: settings.show_confidence_indicators ?? true,
        });
        
      } catch (error) {
        console.error('Failed to load data from API:', error);
        setApiError(error.message);
        
        // Fall back to localStorage
        const savedEducation = localStorage.getItem('adminEducationSections');
        const savedBlog = localStorage.getItem('adminBlogPosts');
        const savedSettings = localStorage.getItem('adminDisplaySettings');
        
        if (savedEducation) setEducationSections(JSON.parse(savedEducation));
        if (savedBlog) setBlogPosts(JSON.parse(savedBlog));
        if (savedSettings) setDisplaySettings(JSON.parse(savedSettings));
      }
      
      // Load auth state
      const savedAuth = sessionStorage.getItem('adminAuth');
      if (savedAuth === 'true') {
        setIsAuthenticated(true);
      }
      
      // Load questions from localStorage (these stay client-side for now)
      const savedQuestions = localStorage.getItem('adminQuestions');
      if (savedQuestions) setQuestions(JSON.parse(savedQuestions));
      
      setIsLoading(false);
    };
    
    loadData();
  }, []);

  const login = useCallback((password) => {
    if (password === DEFAULT_ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      sessionStorage.setItem('adminAuth', 'true');
      return true;
    }
    return false;
  }, []);

  const logout = useCallback(() => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('adminAuth');
  }, []);

  // Question management (stays in localStorage for now)
  const updateQuestion = useCallback((questionId, updates) => {
    setQuestions(prev => {
      const newQuestions = prev.map(q => 
        q.id === questionId ? { ...q, ...updates } : q
      );
      return newQuestions;
    });
    setHasUnsavedChanges(true);
  }, []);

  const updateQuestionOption = useCallback((questionId, optionId, updates) => {
    setQuestions(prev => {
      const newQuestions = prev.map(q => {
        if (q.id === questionId) {
          return {
            ...q,
            options: q.options.map(opt =>
              opt.id === optionId ? { ...opt, ...updates } : opt
            ),
          };
        }
        return q;
      });
      return newQuestions;
    });
    setHasUnsavedChanges(true);
  }, []);

  const toggleQuestionEnabled = useCallback((questionId) => {
    setQuestions(prev => {
      const newQuestions = prev.map(q =>
        q.id === questionId ? { ...q, enabled: !q.enabled } : q
      );
      return newQuestions;
    });
    setHasUnsavedChanges(true);
  }, []);

  // Display settings - uses API
  const updateDisplaySetting = useCallback(async (key, value) => {
    setDisplaySettings(prev => ({
      ...prev,
      [key]: value,
    }));
    setHasUnsavedChanges(true);
  }, []);

  // Education section management - uses API
  const updateEducationSection = useCallback(async (sectionId, updates) => {
    try {
      await educationApi.update(sectionId, updates);
      setEducationSections(prev => prev.map(s =>
        s.id === sectionId ? { ...s, ...updates } : s
      ));
    } catch (error) {
      console.error('Failed to update section:', error);
      // Update locally anyway
      setEducationSections(prev => prev.map(s =>
        s.id === sectionId ? { ...s, ...updates } : s
      ));
      setHasUnsavedChanges(true);
    }
  }, []);

  const addEducationSection = useCallback(async (section) => {
    try {
      const newSection = await educationApi.create(section);
      setEducationSections(prev => [...prev, newSection]);
    } catch (error) {
      console.error('Failed to create section:', error);
      // Add locally
      const localSection = {
        ...section,
        id: section.id || `section-${Date.now()}`,
        order: educationSections.length + 1,
        published: true,
      };
      setEducationSections(prev => [...prev, localSection]);
      setHasUnsavedChanges(true);
    }
  }, [educationSections.length]);

  const deleteEducationSection = useCallback(async (sectionId) => {
    try {
      await educationApi.delete(sectionId);
      setEducationSections(prev => prev.filter(s => s.id !== sectionId));
    } catch (error) {
      console.error('Failed to delete section:', error);
      // Delete locally
      setEducationSections(prev => prev.filter(s => s.id !== sectionId));
      setHasUnsavedChanges(true);
    }
  }, []);

  const reorderEducationSections = useCallback((newOrder) => {
    setEducationSections(newOrder);
    setHasUnsavedChanges(true);
  }, []);

  // Blog post management - uses API
  const updateBlogPost = useCallback(async (postId, updates) => {
    try {
      // Map frontend field names to backend field names
      const apiUpdates = {};
      if (updates.title !== undefined) apiUpdates.title = updates.title;
      if (updates.slug !== undefined) apiUpdates.slug = updates.slug;
      if (updates.seo_title !== undefined) apiUpdates.seo_title = updates.seo_title;
      if (updates.meta_description !== undefined) apiUpdates.meta_description = updates.meta_description;
      if (updates.content !== undefined) apiUpdates.content = updates.content;
      if (updates.excerpt !== undefined) apiUpdates.excerpt = updates.excerpt;
      if (updates.author !== undefined) apiUpdates.author = updates.author;
      if (updates.tags !== undefined) apiUpdates.tags = updates.tags;
      if (updates.featured_image !== undefined) apiUpdates.featured_image = updates.featured_image;
      if (updates.published !== undefined) apiUpdates.is_published = updates.published;
      
      await blogApi.update(postId, apiUpdates);
      setBlogPosts(prev => prev.map(p =>
        p.id === postId ? { ...p, ...updates } : p
      ));
    } catch (error) {
      console.error('Failed to update post:', error);
      setBlogPosts(prev => prev.map(p =>
        p.id === postId ? { ...p, ...updates } : p
      ));
      setHasUnsavedChanges(true);
    }
  }, []);

  const addBlogPost = useCallback(async (post) => {
    try {
      // Generate slug from title if not provided
      const slug = post.slug || post.title.toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/[\s_]+/g, '-')
        .replace(/-+/g, '-')
        .trim();
      
      const newPostData = {
        title: post.title,
        slug: slug,
        seo_title: post.seo_title || post.title,
        meta_description: post.meta_description || post.excerpt || '',
        content: post.content || '',
        excerpt: post.excerpt || '',
        author: post.author || 'GoCompareAI',
        tags: post.tags || [],
        is_published: post.published ?? true,
        featured_image: post.featured_image || null,
      };
      
      const result = await blogApi.create(newPostData);
      const newPost = {
        ...newPostData,
        id: result.id,
        publishedAt: new Date().toISOString(),
        published: newPostData.is_published,
      };
      setBlogPosts(prev => [newPost, ...prev]);
    } catch (error) {
      console.error('Failed to create post:', error);
      const localPost = {
        ...post,
        id: post.id || `post-${Date.now()}`,
        publishedAt: new Date().toISOString(),
        published: true,
      };
      setBlogPosts(prev => [localPost, ...prev]);
      setHasUnsavedChanges(true);
    }
  }, []);

  const deleteBlogPost = useCallback(async (postId) => {
    try {
      await blogApi.delete(postId);
      setBlogPosts(prev => prev.filter(p => p.id !== postId));
    } catch (error) {
      console.error('Failed to delete post:', error);
      setBlogPosts(prev => prev.filter(p => p.id !== postId));
      setHasUnsavedChanges(true);
    }
  }, []);

  // Save all changes
  const saveChanges = useCallback(async () => {
    try {
      // Save display settings to API
      await settingsApi.updateDisplay({
        show_reasoning_in_results: displaySettings.showReasoningInResults,
        show_reasoning_in_details: displaySettings.showReasoningInDetails,
        show_reasoning_in_comparison: displaySettings.showReasoningInComparison,
        show_confidence_indicators: displaySettings.showConfidenceIndicators,
      });
      
      // Save questions to localStorage
      localStorage.setItem('adminQuestions', JSON.stringify(questions));
      
      setHasUnsavedChanges(false);
      return true;
    } catch (error) {
      console.error('Failed to save:', error);
      // Save to localStorage as fallback
      localStorage.setItem('adminDisplaySettings', JSON.stringify(displaySettings));
      localStorage.setItem('adminEducationSections', JSON.stringify(educationSections));
      localStorage.setItem('adminBlogPosts', JSON.stringify(blogPosts));
      localStorage.setItem('adminQuestions', JSON.stringify(questions));
      setHasUnsavedChanges(false);
      return true;
    }
  }, [questions, displaySettings, educationSections, blogPosts]);

  const resetToDefaults = useCallback(async () => {
    // Clear localStorage
    localStorage.removeItem('adminQuestions');
    localStorage.removeItem('adminDisplaySettings');
    localStorage.removeItem('adminEducationSections');
    localStorage.removeItem('adminBlogPosts');
    
    setQuestions([]);
    setDisplaySettings(DEFAULT_SETTINGS);
    
    // Reload from API
    try {
      const sections = await educationApi.getAll();
      setEducationSections(sections);
      
      const posts = await blogApi.getAll();
      setBlogPosts(posts.map(p => ({ ...p, publishedAt: p.published_at })));
    } catch (error) {
      console.error('Failed to reload from API:', error);
    }
    
    setHasUnsavedChanges(false);
  }, []);

  const initializeData = useCallback((defaultQuestions) => {
    if (questions.length === 0) {
      setQuestions(defaultQuestions.map(q => ({ ...q, enabled: true })));
    }
  }, [questions.length]);

  const value = {
    isAuthenticated,
    isLoading,
    apiError,
    login,
    logout,
    questions,
    displaySettings,
    educationSections,
    blogPosts,
    hasUnsavedChanges,
    updateQuestion,
    updateQuestionOption,
    toggleQuestionEnabled,
    updateDisplaySetting,
    updateEducationSection,
    addEducationSection,
    deleteEducationSection,
    reorderEducationSections,
    updateBlogPost,
    addBlogPost,
    deleteBlogPost,
    saveChanges,
    resetToDefaults,
    initializeData,
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};
